import React, { useEffect, useState, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { dashboardAPI } from '../utils/api';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import '../css/Dashboard.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export const Dashboard = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalClients: 0,
    totalVendors: 0,
    totalSales: 0,
    totalPurchases: 0,
    totalSalesBalance: 0,
    totalPurchaseOutstanding: 0,
    topSalesExecutive: null
  });
  const [salesChartData, setSalesChartData] = useState(null);
  const [purchaseChartData, setPurchaseChartData] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [topClients, setTopClients] = useState([]);
  const [topClientsLoading, setTopClientsLoading] = useState(false);
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());

  useEffect(() => {
    loadDashboard();
  }, []);

  useEffect(() => {
    loadTopClients(selectedMonth, selectedYear);
  }, [selectedMonth, selectedYear]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const res = await dashboardAPI.getStats();
      if (res.data.success) {
        const data = res.data;
        setStats(data.stats);
        setRecentActivity(data.recentActivity);

        // Process Charts
        processCharts(data.charts);
      }
    } catch (error) {
      // console.error('Dashboard load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTopClients = async (month, year) => {
    try {
      setTopClientsLoading(true);
      const res = await dashboardAPI.getTopClients(month, year);
      if (res.data.success) {
        setTopClients(res.data.data);
      }
    } catch (error) {
      setTopClients([]);
    } finally {
      setTopClientsLoading(false);
    }
  };

  const processCharts = (charts) => {
    // Sales Chart
    if (charts.monthlySales) {
      setSalesChartData({
        labels: charts.monthlySales.map(d => d.month),
        datasets: [{
          label: 'Sales',
          data: charts.monthlySales.map(d => d.total),
          borderColor: '#0d6efd',
          backgroundColor: 'rgba(13, 110, 253, 0.1)',
          fill: true,
          tension: 0.4
        }]
      });
    }

    // Purchase Chart
    if (charts.monthlyPurchases) {
      setPurchaseChartData({
        labels: charts.monthlyPurchases.map(d => d.month),
        datasets: [{
          label: 'Purchases',
          data: charts.monthlyPurchases.map(d => d.total),
          backgroundColor: (context) => {
            const ctx = context.chart.ctx;
            const gradient = ctx.createLinearGradient(0, 0, 0, 300);
            gradient.addColorStop(0, '#1b2fe7');
            gradient.addColorStop(1, '#00f2fe');
            return gradient;
          },
          borderRadius: 4
        }]
      });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    // status can be null or string
    const s = (status || '').toLowerCase();
    if (s === 'paid') return <span className="badge bg-success">Paid</span>;
    if (s === 'pending') return <span className="badge bg-warning text-dark">Pending</span>;
    if (s === 'overdue') return <span className="badge bg-danger">Overdue</span>;
    return <span className="badge bg-secondary">{status || 'N/A'}</span>;
  };

  if (loading) {
    return <div className="p-5 text-center">Loading Dashboard...</div>;
  }

  return (
    <div className="dashboard-container">
      {/* Header */}
      <div className="dashboard-header mb-4">
        <h1 className="dashboard-title">Dashboard</h1>
        <p className="dashboard-subtitle">Welcome, {user?.full_name}!</p>
      </div>

      {/* Stats Cards */}
      <div className="row mb-4 align-items-stretch">
        <div className="col-12 col-md-6 col-lg-4 mb-3">
          <div className="stat-card card-clients h-100 d-flex flex-column justify-content-center">
            <div className="stat-content">
              <h6 className="stat-label">Total Clients</h6>
              <h2 className="stat-value">{stats.totalClients}</h2>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6 col-lg-4 mb-3">
          <div className="stat-card card-vendors h-100 d-flex flex-column justify-content-center">
            <div className="stat-content">
              <h6 className="stat-label">Total Vendors</h6>
              <h2 className="stat-value">{stats.totalVendors}</h2>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6 col-lg-4 mb-3">
          <div className="stat-card card-sales h-100 d-flex flex-column justify-content-center">
            <div className="stat-content">
              <h6 className="stat-label">Total Sales</h6>
              <h2 className="stat-value">{formatCurrency(stats.totalSales)}</h2>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6 col-lg-4 mb-3">
          <div className="stat-card card-balance h-100 d-flex flex-column justify-content-center">
            <div className="stat-content">
              <h6 className="stat-label">Total Sales Balance</h6>
              <h2 className="stat-value">{formatCurrency(stats.totalSalesBalance)}</h2>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6 col-lg-4 mb-3">
          <div className="stat-card card-purchases h-100 d-flex flex-column justify-content-center">
            <div className="stat-content">
              <h6 className="stat-label">Total Purchases</h6>
              <h2 className="stat-value">{formatCurrency(stats.totalPurchases)}</h2>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6 col-lg-4 mb-3">
          <div className="stat-card card-outstanding h-100 d-flex flex-column justify-content-center">
            <div className="stat-content">
              <h6 className="stat-label">Total Purchase Outstanding</h6>
              <h2 className="stat-value">{formatCurrency(stats.totalPurchaseOutstanding)}</h2>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6 col-lg-4 mb-3">
          <div className="stat-card card-top-executive h-100 d-flex flex-column justify-content-center">
            <div className="stat-content">
              <h6 className="stat-label">Top Sales Exec (Month)</h6>
              <h2 className="stat-value" style={{ fontSize: stats.topSalesExecutive ? '1.5rem' : '1.5rem', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {stats.topSalesExecutive ? stats.topSalesExecutive.executive_name : 'N/A'}
              </h2>
              {stats.topSalesExecutive && (
                <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>{formatCurrency(stats.topSalesExecutive.total_sales)}</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="row mb-4">
        {/* Sales Overview Chart */}
        <div className="col-md-6 mb-3">
          <div className="chart-card">
            <div className="chart-header">
              <h5 className="chart-title">Monthly Sales Overview</h5>
            </div>
            <div className="chart-container">
              {salesChartData ? <Line data={salesChartData} options={{ responsive: true, maintainAspectRatio: false }} /> : <p className="text-muted text-center py-5">No Data Available</p>}
            </div>
          </div>
        </div>

        {/* Purchase Overview Chart */}
        <div className="col-md-6 mb-3">
          <div className="chart-card">
            <div className="chart-header">
              <h5 className="chart-title">Monthly Purchase Overview</h5>
            </div>
            <div className="chart-container">
              {purchaseChartData ? <Bar data={purchaseChartData} options={{ responsive: true, maintainAspectRatio: false }} /> : <p className="text-muted text-center py-5">No Data Available</p>}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity Table - Commented out as requested */}
      {/* 
      <div className="row">
        <div className="col-12">
          <div className="activity-card">
            <h5 className="activity-title">Recent Activity</h5>
            <div className="table-responsive">
              <table className="activity-table">
                <thead>
                  <tr>
                    <th>TYPE</th>
                    <th>CLIENT/VENDOR</th>
                    <th>REFERENCE</th>
                    <th>AMOUNT</th>
                    <th>DATE</th>
                  </tr>
                </thead>
                <tbody>
                  {recentActivity.map((activity, idx) => (
                    <tr key={idx}>
                      <td>
                        <span className={`badge ${activity.type === 'sale' ? 'bg-success' : 'bg-primary'}`}>
                          {activity.type === 'sale' ? 'Sale' : 'Purchase'}
                        </span>
                      </td>
                      <td>{activity.client_name || 'Unknown'}</td>
                      <td>{activity.reference || '-'}</td>
                      <td>{formatCurrency(activity.amount)}</td>
                      <td>{new Date(activity.date).toLocaleDateString()}</td>
                    </tr>
                  ))}
                  {recentActivity.length === 0 && (
                    <tr><td colSpan="5" className="text-center">No recent activity</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      */}
      {/* Monthly Top 10 Clients Table */}
      <div className="row mt-2">
        <div className="col-12">
          <div className="top-clients-card">
            <div className="top-clients-header">
              <div>
                <h5 className="top-clients-title">🏆 Month-wise Top 10 Clients by Sales</h5>
                <p className="top-clients-subtitle">Ranked by total sales amount for the selected month</p>
              </div>
              <div className="top-clients-filter">
                <select
                  id="topClientsMonth"
                  className="month-select"
                  value={selectedMonth}
                  onChange={e => setSelectedMonth(Number(e.target.value))}
                >
                  {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => (
                    <option key={i+1} value={i+1}>{m}</option>
                  ))}
                </select>
                <select
                  id="topClientsYear"
                  className="month-select"
                  value={selectedYear}
                  onChange={e => setSelectedYear(Number(e.target.value))}
                >
                  {[now.getFullYear() - 1, now.getFullYear()].map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>

            {topClientsLoading ? (
              <div className="top-clients-loading">Loading...</div>
            ) : (
              <div className="table-responsive">
                <table className="top-clients-table">
                  <thead>
                    <tr>
                      <th className="col-rank">#</th>
                      <th className="col-name">Client Name</th>
                      <th className="col-exec">Executive Officer</th>
                      <th className="col-amount">Sales Amount</th>
                      <th className="col-bar">Sales Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topClients.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="top-clients-empty">
                          No sales data found for selected month.
                        </td>
                      </tr>
                    ) : (() => {
                      const maxSales = Math.max(...topClients.map(c => parseFloat(c.total_sales) || 0));
                      return topClients.map((client, idx) => {
                        const sales = parseFloat(client.total_sales) || 0;
                        const pct = maxSales > 0 ? (sales / maxSales) * 100 : 0;
                        return (
                          <tr key={idx} className={idx === 0 ? 'top-client-row-first' : ''}>
                            <td className="col-rank">
                              <span className={`rank-badge rank-${idx + 1}`}>
                                {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${idx + 1}`}
                              </span>
                            </td>
                            <td className="col-name client-name-cell">{client.client_name}</td>
                            <td className="col-exec executive-name-cell">
                              {client.executive_name
                                ? <span className="exec-badge">{client.executive_name}</span>
                                : <span style={{ color: '#bbb' }}>—</span>}
                            </td>
                            <td className="col-amount sales-amount-cell">{formatCurrency(sales)}</td>
                            <td className="col-bar">
                              <div className="sales-bar-wrapper">
                                <div
                                  className="sales-bar-fill"
                                  style={{ width: `${pct.toFixed(1)}%`, background: idx === 0 ? 'linear-gradient(90deg,#f7b211,#ff6b35)' : idx === 1 ? 'linear-gradient(90deg,#6dd5fa,#2980b9)' : idx === 2 ? 'linear-gradient(90deg,#a8edea,#43b89c)' : 'linear-gradient(90deg,#d3cce3,#9fa8da)' }}
                                />
                                <span className="sales-bar-pct">{pct.toFixed(0)}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                  {topClients.length > 0 && (
                    <tfoot>
                      <tr>
                        <td colSpan="3" style={{ fontWeight: 700, paddingLeft: '10px', color: '#333' }}>Total (Top 10)</td>
                        <td className="col-amount" style={{ fontWeight: 700, color: '#1a7f37' }}>
                          {formatCurrency(topClients.reduce((s, c) => s + (parseFloat(c.total_sales) || 0), 0))}
                        </td>
                        <td className="col-bar" />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
